import type { CSSProperties, ReactNode } from 'react';

/**
 * Skeleton-loader building blocks + prebuilt page skeletons.
 *
 * Every page in the app (home, listing, detail, checkout, success)
 * shows one of these while content loads. The animation comes from
 * the global `@keyframes skeletonPulse` in `globals.css`, so the
 * components don't depend on any per-route CSS being imported.
 *
 * Two ways this gets used:
 *   1. Next.js `loading.tsx` files in each route segment — these
 *      fire during navigation transitions and during streaming SSR
 *      before the page's `default` export resolves.
 *   2. In-component fallbacks on `'use client'` pages that fetch
 *      their own data via `useEffect`. The route-level loading.tsx
 *      doesn't help once the page is mounted, so those pages render
 *      `<DetailSkeleton />` / `<CheckoutSkeleton />` / etc. while
 *      their own state machines are still in the `loading` branch.
 */

const SKELETON_BG = '#eceaf1';
const SKELETON_DARK_BG = '#dcd5e3';
const PULSE_STYLE: CSSProperties = {
  animation: 'skeletonPulse 1.5s ease-in-out infinite',
};

interface BoxProps {
  width?: number | string;
  height?: number | string;
  radius?: number;
  mb?: number | string;
  mt?: number | string;
  className?: string;
  style?: CSSProperties;
}

/** Rounded rectangle placeholder. */
export function SkeletonBox({
  width = '100%',
  height = 16,
  radius = 6,
  mb,
  mt,
  className,
  style,
}: BoxProps) {
  return (
    <div
      className={className}
      style={{
        width,
        height,
        background: SKELETON_BG,
        borderRadius: radius,
        marginBottom: mb,
        marginTop: mt,
        ...PULSE_STYLE,
        ...style,
      }}
    />
  );
}

interface TextProps {
  /** Number of placeholder text lines to render. */
  lines?: number;
  /** Width of the LAST line — earlier lines stay at 100%. */
  lastLineWidth?: string | number;
  lineHeight?: number;
  gap?: number;
  className?: string;
}

/** Multi-line text placeholder, mimics a paragraph. */
export function SkeletonText({
  lines = 1,
  lastLineWidth = '70%',
  lineHeight = 12,
  gap = 8,
  className,
}: TextProps) {
  return (
    <div className={className}>
      {Array.from({ length: lines }).map((_, i) => (
        <SkeletonBox
          key={i}
          width={i === lines - 1 ? lastLineWidth : '100%'}
          height={lineHeight}
          mb={i === lines - 1 ? 0 : gap}
        />
      ))}
    </div>
  );
}

/** Circular placeholder (avatar / icon). */
export function SkeletonCircle({ size = 32 }: { size?: number }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        background: SKELETON_BG,
        borderRadius: '50%',
        ...PULSE_STYLE,
      }}
    />
  );
}

/** Larger banner / image placeholder. Used for hero areas. */
export function SkeletonBanner({ height = 280 }: { height?: number }) {
  return (
    <div
      style={{
        width: '100%',
        height,
        background: SKELETON_DARK_BG,
        borderRadius: 12,
        ...PULSE_STYLE,
      }}
    />
  );
}

// ─── Pre-built page skeletons ──────────────────────────────────────────

function PageShell({ children }: { children: ReactNode }) {
  return (
    <div className="container py-4" aria-busy="true" aria-live="polite">
      {children}
    </div>
  );
}

/** Home page (sports/page.tsx) — hero search + featured rows of cards. */
export function HomeSkeleton() {
  return (
    <PageShell>
      <SkeletonBanner height={320} />
      <div style={{ height: 32 }} />
      <SkeletonBox width="40%" height={28} mb={16} />
      <div className="row">
        {[0, 1, 2, 3].map((i) => (
          <div className="col-md-3 mb-4" key={`tour-${i}`}>
            <SkeletonBox height={180} radius={12} mb={12} />
            <SkeletonBox width="70%" height={16} />
          </div>
        ))}
      </div>
      <div style={{ height: 24 }} />
      <SkeletonBox width="40%" height={28} mb={16} />
      <div className="row">
        {[0, 1, 2, 3].map((i) => (
          <div className="col-md-3 mb-4" key={`city-${i}`}>
            <SkeletonBox height={180} radius={12} mb={12} />
            <SkeletonBox width="60%" height={16} />
          </div>
        ))}
      </div>
    </PageShell>
  );
}

/** Listing page (sports/events, sports/city/[slug], etc.) — hero, filter
 *  row, then a grid of event cards. */
export function ListingSkeleton() {
  return (
    <PageShell>
      <SkeletonBanner height={220} />
      <div style={{ height: 24 }} />
      {/* Filter chips row */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        {[100, 120, 110, 130, 100].map((w, i) => (
          <SkeletonBox key={i} width={w} height={36} radius={20} />
        ))}
      </div>
      <div className="row">
        {Array.from({ length: 6 }).map((_, i) => (
          <div className="col-lg-4 pb-4" key={i}>
            <div
              style={{
                padding: 20,
                borderRadius: 14,
                background: '#fff',
                border: '1px solid #eee',
              }}
            >
              <SkeletonBox width="40%" height={14} mb={12} />
              <SkeletonBox width="90%" height={22} mb={10} />
              <SkeletonBox width="70%" height={14} mb={8} />
              <SkeletonBox width="80%" height={14} mb={16} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <SkeletonBox width="35%" height={20} />
                <SkeletonBox width={90} height={32} radius={20} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </PageShell>
  );
}

/** Sports detail page — breadcrumb, hero info, then the ticket list. */
export function DetailSkeleton() {
  return (
    <PageShell>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        <SkeletonBox width={60} height={14} />
        <SkeletonBox width={90} height={14} />
        <SkeletonBox width={120} height={14} />
        <SkeletonBox width={180} height={14} />
      </div>

      {/* Event header */}
      <SkeletonBox width="60%" height={32} mb={14} />
      <SkeletonText lines={2} gap={8} lastLineWidth="55%" />
      <div style={{ height: 24 }} />

      {/* Two-column: ticket list + venue/info panel */}
      <div className="row">
        <div className="col-lg-8 mb-4">
          {/* Filter buttons */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <SkeletonBox width={120} height={36} radius={20} />
            <SkeletonBox width={120} height={36} radius={20} />
            <SkeletonBox width={120} height={36} radius={20} />
          </div>

          {/* Ticket cards */}
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                background: '#f6e2ff',
                padding: 20,
                borderRadius: 14,
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 8,
                  marginBottom: 12,
                }}
              >
                <div style={{ flex: 1 }}>
                  <SkeletonBox width="80%" height={18} mb={6} />
                  <SkeletonBox width="50%" height={14} />
                </div>
                <SkeletonBox width={100} height={20} />
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 12,
                }}
              >
                <SkeletonBox width={140} height={32} radius={6} />
                <SkeletonBox width={110} height={32} radius={8} />
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  marginTop: 14,
                  alignItems: 'center',
                }}
              >
                <SkeletonBox width={70} height={28} radius={20} />
                <SkeletonBox width={120} height={28} radius={20} />
                <div style={{ marginLeft: 'auto' }}>
                  <SkeletonBox width={70} height={32} radius={20} />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="col-lg-4 mb-4">
          <div
            style={{
              background: '#fff',
              padding: 20,
              borderRadius: 14,
              border: '1px solid #eee',
            }}
          >
            <SkeletonBox width="50%" height={22} mb={14} />
            <SkeletonText lines={3} gap={10} lastLineWidth="60%" />
            <div style={{ height: 20 }} />
            <SkeletonBox height={180} radius={8} />
          </div>
        </div>
      </div>
    </PageShell>
  );
}

/** Checkout / payment page — left column form + right column order summary. */
export function CheckoutSkeleton() {
  return (
    <PageShell>
      {/* Steps bar */}
      <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginBottom: 28 }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SkeletonCircle size={28} />
            <SkeletonBox width={90} height={14} />
          </div>
        ))}
      </div>

      <div className="row">
        <div className="col-lg-8 mb-4">
          {/* Contact details card */}
          <div
            style={{
              background: '#fff',
              padding: 24,
              borderRadius: 14,
              border: '1px solid #eee',
              marginBottom: 20,
            }}
          >
            <SkeletonBox width="35%" height={20} mb={16} />
            <div className="row">
              <div className="col-md-6 mb-3"><SkeletonBox height={42} /></div>
              <div className="col-md-6 mb-3"><SkeletonBox height={42} /></div>
              <div className="col-md-12 mb-3"><SkeletonBox height={42} /></div>
              <div className="col-md-12 mb-3"><SkeletonBox height={42} /></div>
              <div className="col-md-12 mb-3"><SkeletonBox height={42} /></div>
              <div className="col-md-6 mb-3"><SkeletonBox height={42} /></div>
              <div className="col-md-6 mb-3"><SkeletonBox height={42} /></div>
            </div>
          </div>
          {/* Attendees card */}
          <div
            style={{
              background: '#fff',
              padding: 24,
              borderRadius: 14,
              border: '1px solid #eee',
            }}
          >
            <SkeletonBox width="40%" height={20} mb={16} />
            <SkeletonBox height={56} mb={12} />
            <SkeletonBox height={56} mb={12} />
            <SkeletonBox height={56} />
          </div>
        </div>
        <div className="col-lg-4 mb-4">
          {/* Order summary */}
          <div
            style={{
              background: '#fff',
              padding: 24,
              borderRadius: 14,
              border: '1px solid #eee',
            }}
          >
            <SkeletonBox width="50%" height={22} mb={16} />
            <SkeletonText lines={2} gap={8} lastLineWidth="60%" />
            <div style={{ height: 16 }} />
            <SkeletonBox height={1} />
            <div style={{ height: 16 }} />
            <SkeletonText lines={3} gap={10} lastLineWidth="50%" />
            <div style={{ height: 16 }} />
            <SkeletonBox height={1} />
            <div style={{ height: 16 }} />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <SkeletonBox width="30%" height={22} />
              <SkeletonBox width="30%" height={22} />
            </div>
            <div style={{ height: 20 }} />
            <SkeletonBox height={44} radius={8} />
          </div>
        </div>
      </div>
    </PageShell>
  );
}

/** Booking confirmation / success page — large confirmation header, then
 *  detail cards for the booking. */
export function SuccessSkeleton() {
  return (
    <PageShell>
      <div style={{ textAlign: 'center', padding: '32px 0' }}>
        <SkeletonCircle size={72} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 32 }}>
        <SkeletonBox width="60%" height={36} />
      </div>
      <div className="row">
        <div className="col-lg-8 mb-4">
          <div
            style={{
              background: '#fff',
              padding: 24,
              borderRadius: 14,
              border: '1px solid #eee',
            }}
          >
            <SkeletonText lines={3} gap={12} lastLineWidth="65%" />
          </div>
        </div>
        <div className="col-lg-4 mb-4">
          <div
            style={{
              background: '#fff',
              padding: 24,
              borderRadius: 14,
              border: '1px solid #eee',
            }}
          >
            <SkeletonText lines={2} gap={10} lastLineWidth="55%" />
          </div>
        </div>
      </div>
      <div
        style={{
          background: '#fff',
          padding: 24,
          borderRadius: 14,
          border: '1px solid #eee',
          marginTop: 8,
        }}
      >
        <SkeletonBox width="35%" height={20} mb={16} />
        <SkeletonText lines={4} gap={12} lastLineWidth="70%" />
      </div>
    </PageShell>
  );
}
